// In-app alert sounds and vibration (issue #3).
//
// Sounds are synthesized with Web Audio rather than shipped as files: no
// assets to cache, no network fetch at the moment a call rings, and the tones
// can be tuned in code.
//
// Browsers only let a page make sound after the user has interacted with it,
// so the AudioContext is created and resumed on the first tap/keypress.

let context: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (context) return context;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  context = new Ctor();
  return context;
}

export function setupAudioUnlock(): () => void {
  const unlock = () => {
    const ctx = audioContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  };
  const events = ['pointerdown', 'keydown', 'touchend'] as const;
  events.forEach((name) =>
    window.addEventListener(name, unlock, { passive: true })
  );
  return () =>
    events.forEach((name) => window.removeEventListener(name, unlock));
}

interface Note {
  frequency: number;
  start: number; // seconds from now
  duration: number;
  gain: number;
  type?: OscillatorType;
  // Hold the level for the whole note (a tone) instead of decaying (a bell).
  sustain?: boolean;
}

interface Playing {
  oscillator: OscillatorNode;
  gain: GainNode;
}

/** Schedules the notes; returns what's playing so a ring can be cut short. */
function playNotes(notes: Note[]): Playing[] {
  const ctx = audioContext();
  if (!ctx || ctx.state !== 'running') return [];
  const now = ctx.currentTime;

  return notes.map((note) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = note.type ?? 'sine';
    oscillator.frequency.value = note.frequency;

    // Fast attack, exponential decay: a bell-like "ding" rather than a beep.
    const start = now + note.start;
    const end = start + note.duration;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(note.gain, start + 0.015);
    if (note.sustain) gain.gain.setValueAtTime(note.gain, end - 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + note.duration + 0.05);
    return { oscillator, gain };
  });
}

/** Silences notes now, with a 30 ms fade so it doesn't click. */
function silence(playing: Playing[]) {
  const ctx = audioContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  for (const { oscillator, gain } of playing) {
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.03);
      oscillator.stop(now + 0.04);
    } catch {
      // Already stopped.
    }
  }
}

// Two rising notes, about a third of a second.
export function playMessageSound() {
  playNotes([
    { frequency: 880, start: 0, duration: 0.18, gain: 0.18 },
    { frequency: 1318.5, start: 0.09, duration: 0.3, gain: 0.14 },
  ]);
}

export function vibrate(pattern: number | number[]) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Unsupported (iOS) or blocked until the user interacts: ignore.
  }
}

export function stopVibration() {
  vibrate(0);
}

export const MESSAGE_VIBRATION = [120, 80, 120];
const RING_VIBRATION = [700, 300, 700];

// Repeats until the returned stop function is called. Stopping also silences
// the ring that's sounding: the 2 s ringback used to play out after a call
// was cancelled, like one more quieter ring (2026-10-22).
function loop(play: () => Playing[], intervalMs: number): () => void {
  let playing = play();
  const timer = window.setInterval(() => {
    playing = play();
  }, intervalMs);
  return () => {
    window.clearInterval(timer);
    silence(playing);
    playing = [];
  };
}

// Incoming call: a rising arpeggio every 2.5s, with vibration on each ring.
export function startRingtone(options: { sound: boolean; vibrate: boolean }) {
  return loop(() => {
    const playing = options.sound
      ? playNotes([
          { frequency: 659.25, start: 0, duration: 0.35, gain: 0.22 },
          { frequency: 783.99, start: 0.18, duration: 0.35, gain: 0.22 },
          { frequency: 1046.5, start: 0.36, duration: 0.6, gain: 0.22 },
          { frequency: 659.25, start: 1.0, duration: 0.35, gain: 0.22 },
          { frequency: 783.99, start: 1.18, duration: 0.35, gain: 0.22 },
          { frequency: 1046.5, start: 1.36, duration: 0.6, gain: 0.22 },
        ])
      : [];
    if (options.vibrate) vibrate(RING_VIBRATION);
    return playing;
  }, 2500);
}

// "Come now" for a gathering (D278, D286): a short, warm three-note tune, a
// falling-then-rising figure on softer, lower notes than the call ringtone so
// it can't be taken for a call. Played twice, then it stops by itself.
export function startGatheringTune(options: {
  sound: boolean;
  vibrate: boolean;
}): () => void {
  const play = (): Playing[] =>
    options.sound
      ? playNotes([
          { frequency: 523.25, start: 0, duration: 0.45, gain: 0.2 },
          { frequency: 392, start: 0.3, duration: 0.45, gain: 0.2 },
          { frequency: 659.25, start: 0.6, duration: 0.8, gain: 0.2 },
        ])
      : [];
  let playing = play();
  if (options.vibrate) vibrate([300, 150, 300]);
  const timer = window.setTimeout(() => {
    playing = play();
    if (options.vibrate) vibrate([300, 150, 300]);
  }, 2000);
  return () => {
    window.clearTimeout(timer);
    silence(playing);
    playing = [];
  };
}

// Outgoing call: the classic ringback (440 + 480 Hz, 2s on, 4s off), loud
// enough to hear from a phone's speaker (D278; 0.05 was barely audible).
export function startRingback() {
  return loop(
    () =>
      playNotes([
        { frequency: 440, start: 0, duration: 2, gain: 0.15, sustain: true },
        { frequency: 480, start: 0, duration: 2, gain: 0.15, sustain: true },
      ]),
    6000
  );
}
